import { nullIfEmpty, removeKeysWithEmptyValues } from '../utils/object'
import { actionAddAirway } from './airway'
import {
  actionPresetInfusionDrug,
  selectInfusionDrugsForMacro,
} from './infusion-drug'
import { actionUpdateFirstIvSite, selectIvAccessData } from './fluids'
import { actionPresetLocalDrug, selectLocalsForMacro } from './local-drug'
import {
  actionPresetOralSedative,
  selectOralSedativesForMacro,
} from './oral-sedative'
import { actionPresetParenDrug, selectParenDrugsForMacro } from './paren-drug'
import { actionAddPosition } from './position'
import { actionAddPreMed, selectPreMedsForMacro } from './pre-meds'
import {
  actionSetAllSystemCategoriesToNo,
  selectIsHealthy,
} from './systems-review'
import {
  actionIgnoreVitalType,
  actionSelectVitalType,
  selectVitalTypesForMacro,
} from './vital-type'
import { caseKeysFromHhx } from '../utils/case-hhx-utils'

const saveableItems = [
  'sedationLevel',
  'ivEnabled',
  'airwayEnabled',
  'recorderName',
  'present',
  'userRole',
  'asaClassification',
  'mallampati',
  'weightDistribution',
  'surgeonName',
  'sedationProviderName',
  'reasonForSedation',
  'pregnant',
  'breastfeeding',
  'breastfeedingInstructionsGiven',
  'lastFood',
  'lastDrink',
  'procedureNotes',
  'recoveryNotes',
  'preProcedureRiskAssessment',
  'calculateDrugWaste',
  'dischargeScoreType',
]

/**
 * @param {CaseData} caseData
 * @returns {Macro}
 */
export const createMacroFromCase = caseData => {
  const {
    aw,
    pos,
    preMedsUsed,
    ivLocations,
    systemsReviewSkipped,
    startedOnRoomAir,
    vitalsTrackingEnabled,
  } = caseData

  const result = {
    copied: {},
  }

  // straight copy
  for (const key of saveableItems) {
    if (
      caseData.hasOwnProperty(key) &&
      caseData[key] != null &&
      caseData[key] !== ''
    ) {
      result.copied[key] = caseData[key]
    }
  }

  // this is also added to "copied" but we only do this if true, not false
  if (systemsReviewSkipped) {
    result.copied.systemsReviewSkipped = true
  }

  // this is also added to "copied" but we only do this if true, not false
  if (startedOnRoomAir) {
    result.copied.startedOnRoomAir = true
  }

  // also copied but only if false
  if (preMedsUsed === false) {
    result.copied.preMedsUsed = preMedsUsed
  }

  // we only store the vitalsTrackingEnabled if it's false
  // otherwise we can safely assume it's true
  if (vitalsTrackingEnabled === false) {
    result.copied.vitalsTrackingEnabled = false
  }

  // if they've said "no" to everything in systems review
  if (selectIsHealthy(caseData)) {
    result.healthyPatient = true
  }

  result.preMeds = selectPreMedsForMacro(caseData)
  result.vitalTypes = selectVitalTypesForMacro(caseData)
  result.localDrugs = selectLocalsForMacro(caseData)
  result.parenDrugs = selectParenDrugsForMacro(caseData)
  result.infusionDrugs = selectInfusionDrugsForMacro(caseData)
  result.oralSedatives = selectOralSedativesForMacro(caseData)

  if (aw) {
    result.airways = Object.values(aw)
      .map(awDetails => {
        const toReturn = {}
        for (const key in awDetails) {
          // things we don't want to include
          if (
            key === 'placementComp' ||
            key === 'timePlaced' ||
            key === 'timeRemoved' ||
            key === 'preRemovalSpo2' ||
            key === 'postRemovalSpo2'
          ) {
            continue
          }
          const value = nullIfEmpty(awDetails[key], true)
          if (value !== null) {
            toReturn[key] = value
          }
        }

        return nullIfEmpty(toReturn)
      })
      .filter(Boolean)
  }

  if (pos) {
    /** @type {Macro['pos']} */
    result.pos = Object.values(pos).map(item => item.name)
  }

  if (ivLocations) {
    const ivAccess = selectIvAccessData(caseData)
    // we don't keep if if it's zero because it's never
    // part of a macro if it's zero.
    if (ivAccess.attempts === 0) {
      delete ivAccess.attempts
    }
    result.iv = removeKeysWithEmptyValues(ivAccess)
  }

  // remove any keys that are empty objects
  for (const key in result) {
    const value = result[key]
    const isObject = typeof value === 'object'
    if (isObject && nullIfEmpty(value) === null) {
      delete result[key]
    }
  }

  return result
}

/**
 * @param {Macro} macro
 * @returns {any} Updates to apply
 */
export const actionGetUpdatesForMacro = macro => {
  const {
    copied,
    airways,
    pos,
    iv,
    preMeds,
    healthyPatient,
    vitalTypes,
    localDrugs,
    parenDrugs,
    infusionDrugs,
    oralSedatives,
  } = macro
  const updates = {}
  if (copied) {
    for (const key in copied) {
      updates[key] = copied[key]
    }
  }

  if (healthyPatient) {
    Object.assign(updates, actionSetAllSystemCategoriesToNo())
  }

  if (airways) {
    for (const airway of airways) {
      const { type, ...rest } = airway
      Object.assign(updates, actionAddAirway(type, rest))
    }
  }

  if (pos) {
    for (const name of pos) {
      Object.assign(updates, actionAddPosition(name))
    }
  }

  if (iv) {
    Object.assign(updates, actionUpdateFirstIvSite(iv))
  }

  if (preMeds) {
    for (const preMed of preMeds) {
      // we want time null here so that we don't enter specific times just by
      // applying the preset
      Object.assign(updates, actionAddPreMed({ ...preMed, time: null }))
    }
  }

  if (vitalTypes) {
    for (const id in vitalTypes) {
      const val = vitalTypes[id]
      if (val === 'selected') {
        Object.assign(updates, actionSelectVitalType(id))
      }
      if (val === 'ignored') {
        Object.assign(updates, actionIgnoreVitalType(id))
      }
    }
  }

  if (localDrugs) {
    Object.assign(updates, ...localDrugs.map(actionPresetLocalDrug))
  }

  if (parenDrugs) {
    Object.assign(
      updates,
      ...parenDrugs.map(drug => actionPresetParenDrug(drug))
    )
  }

  if (infusionDrugs) {
    Object.assign(updates, ...infusionDrugs.map(actionPresetInfusionDrug))
  }

  if (oralSedatives) {
    Object.assign(updates, ...oralSedatives.map(actionPresetOralSedative))
  }

  /**
   * This final check is to make sure we don't put the case file into a weird
   * state where the vitalsTrackingEnabled is false but the sedation level is
   * not local. We still have a selector that will still return the correct
   * value for vitalsTrackingEnabled but this check makes sure the value isn't
   * wrong in the underlying case file.
   */
  if (
    updates.sedationLevel &&
    updates.sedationLevel !== 'Local' &&
    updates.vitalsTrackingEnabled === false
  ) {
    delete updates.vitalsTrackingEnabled
  }

  return updates
}

const combineObject = (a, b) => nullIfEmpty({ ...(a || {}), ...(b || {}) })

const mergeArraysByProp = (a, b, prop) => {
  if (a && b) {
    const entries = {}
    for (const entry of a) {
      entries[entry[prop]] = entry
    }
    for (const entry of b) {
      entries[entry[prop]] = combineObject(entries[entry[prop]], entry)
    }
    return Object.values(entries)
  }

  return a || b
}

/**
 * @param {Macro} a
 * @param {Macro} b
 * @returns {Macro}
 */
const mergeTwoMacros = (a, b) => {
  const result = {}
  result.copied = combineObject(a.copied, b.copied)
  result.iv = combineObject(a.iv, b.iv)
  result.pos = b.pos || a.pos
  result.airways = mergeArraysByProp(a.airways, b.airways, 'type')
  result.vitalTypes = combineObject(a.vitalTypes, b.vitalTypes)
  result.localDrugs = mergeArraysByProp(a.localDrugs, b.localDrugs, 'id')
  result.parenDrugs = mergeArraysByProp(a.parenDrugs, b.parenDrugs, 'id')
  result.preMeds = mergeArraysByProp(a.preMeds, b.preMeds, 'drugId')
  result.infusionDrugs = mergeArraysByProp(
    a.infusionDrugs,
    b.infusionDrugs,
    'id'
  )
  result.healthyPatient = a.healthyPatient || b.healthyPatient || null
  result.oralSedatives = mergeArraysByProp(
    a.oralSedatives,
    b.oralSedatives,
    'id'
  )

  return removeKeysWithEmptyValues(result)
}

/**
 * @param {Macro[]} macros
 * @returns {Macro}
 */
export const mergeMacros = (...macros) => macros.reduce(mergeTwoMacros, {})

/**
 * @param {Macro} macro
 * @returns {Macro}
 */
export const removeHhxItemsFromMacro = macro => {
  const result = JSON.parse(JSON.stringify(macro))
  // remove any copied keys that are in the caseKeysFromHhx
  if (result.copied) {
    for (const key in result.copied) {
      if (caseKeysFromHhx.includes(key)) {
        delete result.copied[key]
      }
    }
    if (nullIfEmpty(result.copied) === null) {
      delete result.copied
    }
  }
  delete result.healthyPatient
  return result
}
